import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { UniversityLogo } from './UniversityLogo'

interface ProgrammeDetails {
  id: string
  name: string
  faculty: string | null
  university: { short_name: string; name: string; logo_url?: string | null }
  description: string | null
  typical_employers: string[]
  provinces: string[]
  career_note: string | null
  salary_range_mzn: string | null
  difficulty_note: string | null
  source_url: string | null
  seats: number | null
  periods_available?: string[]
  seats_by_period?: { laboral?: number; pos_laboral?: number } | null
  /** `subject.active` false means the platform does not teach this exam
   *  component (Desenho, Aptidão Física, Aptidão em Desenho, Inquérito
   *  Sociolinguístico). It still carries real weight in the edital, so it is
   *  listed with its percentage and marked rather than hidden — a candidate
   *  needs to know the component exists even though they must prepare it
   *  elsewhere. Optional so older cached responses, which lack the field,
   *  fall back to treating everything as taught. */
  requirements: { subject: { code: string; name: string; active?: boolean }; variant: string; weight: number }[]
}

interface Props {
  programmeId: string
  onClose: () => void
  onChoose?: () => void       // optional CTA: "Preparar este curso"
  chooseLabel?: string
}

const SUBJECT_EMOJI: Record<string, string> = {
  MAT: '📐', BIO: '🧬', FIS: '⚡', QUI: '🧪', POR: '📝', HIS: '📜',
  GEO: '🌍', ING: '🇬🇧', FIL: '💭', FRA: '🇫🇷', CG: '📚',
}

export const ProgrammeDetailsSheet: React.FC<Props> = ({ programmeId, onClose, onChoose, chooseLabel }) => {
  const [data, setData] = useState<ProgrammeDetails | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api.get<{ programme: ProgrammeDetails }>(`/programmes/${programmeId}/details`)
      .then(r => { if (!cancelled) setData(r.programme) })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [programmeId])

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div
        className="bg-white w-full max-w-md rounded-t-xl sm:rounded-xl max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-start justify-between">
          <div className="flex-1 pr-3">
            {loading || !data ? (
              <div className="h-4 bg-gray-100 rounded animate-pulse w-2/3" />
            ) : (
              <div className="flex items-start gap-3">
                <UniversityLogo
                  shortName={data.university.short_name}
                  name={data.university.name}
                  logoUrl={data.university.logo_url ?? null}
                  size="md"
                />
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-gray-900 leading-tight">{data.name}</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {data.university.short_name}{data.faculty ? ` · ${data.faculty}` : ''}
                  </p>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="text-gray-400 hover:text-gray-600 text-xl leading-none w-8 h-8 flex items-center justify-center"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4 space-y-4">
          {loading && (
            <>
              <div className="h-4 bg-gray-100 rounded animate-pulse" />
              <div className="h-4 bg-gray-100 rounded animate-pulse w-4/5" />
              <div className="h-4 bg-gray-100 rounded animate-pulse w-3/5" />
            </>
          )}

          {!loading && data && (
            <>
              {/* Description */}
              {data.description ? (
                <p className="text-sm text-gray-700 leading-relaxed">{data.description}</p>
              ) : (
                <p className="text-sm text-gray-400 italic">Descrição em breve.</p>
              )}

              {/* Difficulty note (amber) */}
              {data.difficulty_note && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                  ⚠️ {data.difficulty_note}
                </div>
              )}

              {/* Employers */}
              {data.typical_employers.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Onde podes trabalhar</p>
                  <div className="flex flex-wrap gap-1.5">
                    {data.typical_employers.map(e => (
                      <span key={e} className="text-xs bg-indigo-100 text-indigo-700 font-medium px-2.5 py-1 rounded-full">
                        {e}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Provinces */}
              {data.provinces.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Onde estes profissionais fazem falta</p>
                  <p className="text-sm text-gray-700">{data.provinces.join(' · ')}</p>
                </div>
              )}

              {/* Salary */}
              {data.salary_range_mzn && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Faixa salarial indicativa</p>
                  <p className="text-sm text-gray-700 font-medium">{data.salary_range_mzn} MZN/mês</p>
                </div>
              )}

              {/* Requirements */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Exames necessários</p>
                <div className="flex flex-wrap gap-1.5">
                  {data.requirements.map(r => {
                    // `active === false` only. An undefined field means a
                    // cached response from before the API exposed it, which
                    // must keep rendering as a normal taught subject.
                    const naoDisponivel = r.subject.active === false
                    return (
                      <span
                        key={`${r.subject.code}-${r.variant}`}
                        className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                          naoDisponivel
                            ? 'bg-amber-50 text-amber-800 border border-amber-200'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {naoDisponivel ? '📄' : (SUBJECT_EMOJI[r.subject.code] ?? '📘')}{' '}
                        {naoDisponivel ? r.subject.name : r.subject.code}
                        {r.variant ? ' ' + r.variant : ''} · {r.weight}%
                      </span>
                    )
                  })}
                </div>
                {/* One note for the whole group rather than a repeated tag on
                    each chip: a course can carry two untaught components and
                    the chips are already dense at 360px. */}
                {data.requirements.some(r => r.subject.active === false) && (
                  <p className="text-[11px] font-semibold text-amber-800 mt-1.5 leading-snug text-center">
                    Disciplina não disponível no Tutor
                  </p>
                )}
              </div>

              {/* Seats - dual-period courses surface per-period vagas so the
                  student sees both admission pools at once. */}
              {(() => {
                const dual = data.periods_available && data.periods_available.length > 1
                const byPeriod = data.seats_by_period
                if (dual && byPeriod) {
                  return (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Vagas (2026)</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-amber-50 border border-amber-100 rounded-lg p-2.5">
                          <p className="text-[10px] text-amber-700 font-semibold">☀️ LABORAL</p>
                          <p className="text-sm font-semibold text-amber-900">{byPeriod.laboral ?? '-'} vagas</p>
                        </div>
                        <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-2.5">
                          <p className="text-[10px] text-indigo-700 font-semibold">🌙 PÓS-LABORAL</p>
                          <p className="text-sm font-semibold text-indigo-900">{byPeriod.pos_laboral ?? '-'} vagas</p>
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-400 italic mt-1.5">
                        Vagas separadas por período.
                      </p>
                    </div>
                  )
                }
                if (dual && !byPeriod) {
                  return (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Períodos</p>
                      <p className="text-sm text-gray-700">Disponível em Laboral e Pós-Laboral.</p>
                    </div>
                  )
                }
                if (data.seats != null && data.seats > 0) {
                  return (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Vagas (2026)</p>
                      <p className="text-sm text-gray-700">{data.seats} vagas disponíveis</p>
                    </div>
                  )
                }
                return null
              })()}

              {/* Career note */}
              {/* Citation. The employers, provinces and salary above are claims
                  about the world, not things the platform can promise, so they
                  carry their source where one was recorded. noopener/noreferrer
                  because the URL is admin-entered and points off-site. */}
              {data.source_url && (
                <p className="text-[11px] text-gray-400">
                  Fonte:{' '}
                  <a
                    href={data.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-gray-600 break-all"
                  >
                    {(() => {
                      try { return new URL(data.source_url).hostname.replace(/^www\./, '') }
                      catch { return data.source_url }
                    })()}
                  </a>
                </p>
              )}

              {data.career_note && (
                <p className="text-xs text-gray-500 italic">{data.career_note}</p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {onChoose && !loading && data && (
          <div className="sticky bottom-0 bg-white border-t border-gray-100 px-4 py-3">
            <button
              onClick={onChoose}
              className="w-full bg-indigo-600 text-white text-sm font-semibold py-3 rounded-xl hover:bg-indigo-700 transition-colors"
            >
              {chooseLabel ?? 'Preparar este curso'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
